/**
 * Canonical stage normalization for collection recommendations (C1).
 */

import type { CollectionRecommendedStage } from '@shared/collection/collection-recommendation-types';
import { isCollectionRecommendedStage } from '@shared/collection/collection-recommendation-types';

const FORBIDDEN_CANONICAL_INPUTS = new Set(['final', 'final_letter']);

export type NormalizeCollectionRecommendationStageResult =
  | { ok: true; stage: CollectionRecommendedStage }
  | { ok: false; reason: 'unsupported' | 'forbidden' };

/**
 * Normalizes legacy collection stage strings for recommendation persistence.
 *
 * - `final_notice` → `final_notice`
 * - `final_letter` → `final_notice` (read-time compat)
 * - `final` → rejected
 * - unknown → rejected
 */
export function normalizeCollectionRecommendationStage(
  input: string | null | undefined
): NormalizeCollectionRecommendationStageResult {
  const trimmed = input?.trim();
  if (!trimmed) {
    return { ok: false, reason: 'unsupported' };
  }

  if (trimmed === 'final') {
    return { ok: false, reason: 'forbidden' };
  }

  if (trimmed === 'final_letter') {
    return { ok: true, stage: 'final_notice' };
  }

  if (isCollectionRecommendedStage(trimmed)) {
    return { ok: true, stage: trimmed };
  }

  if (FORBIDDEN_CANONICAL_INPUTS.has(trimmed)) {
    return { ok: false, reason: 'forbidden' };
  }

  return { ok: false, reason: 'unsupported' };
}
